import { type PersistentStore, setPersistentEngine, windowPersistentEvents } from '@nanostores/persistent';

import { sentryCaptureException } from '@/utils/sentry';

/**
 * The storage persistent nanostores read and write, over `storage`, where a failed write or delete (a full
 * quota, blocked storage) is reported and dropped. `@nanostores/persistent`'s default engine assigns to
 * `localStorage` directly, so the error would otherwise throw out of the store's setter before the store
 * updates, and take whatever called it down too. The store keeps its new value in memory either way.
 */
export function createBestEffortStorage(storage: Storage): PersistentStore {
  return new Proxy<PersistentStore>(
    {},
    {
      get(_target, key: string) {
        return storage.getItem(key) ?? undefined;
      },
      has(_target, key: string) {
        return storage.getItem(key) !== null;
      },
      set(_target, key: string, value: string) {
        try {
          storage.setItem(key, value);
        } catch (error) {
          sentryCaptureException(new Error('Failed to persist a nanostore', { cause: error }), {
            contexts: { storage: { key } },
          });
        }
        return true;
      },
      deleteProperty(_target, key: string) {
        try {
          storage.removeItem(key);
        } catch (error) {
          sentryCaptureException(new Error('Failed to remove a persisted nanostore', { cause: error }), {
            contexts: { storage: { key } },
          });
        }
        return true;
      },
      // `persistentMap` lists the stored keys with `for...in`.
      ownKeys() {
        return Object.keys(storage);
      },
      getOwnPropertyDescriptor() {
        return { configurable: true, enumerable: true };
      },
    },
  );
}

/** Puts every persistent nanostore on {@link createBestEffortStorage} over `localStorage`. Call once at startup. */
export function installBestEffortPersistentStorage(): void {
  let storage: Storage;
  try {
    storage = window.localStorage;
  } catch {
    // Blocked storage: `@nanostores/persistent` already keeps its stores in memory.
    return;
  }
  setPersistentEngine(createBestEffortStorage(storage), windowPersistentEvents);
}
